/**
 * 华为盯盘策略 · 脑图视图（飞书画板式思维导图：HTML 盒节点 + SVG 贝塞尔连线；
 * CLAUDE.md §前端 UI 须借鉴 LEO v1.0 §2.1 设计 Token / §6.2 Tab / §6.6 浮层）。
 *
 * 为什么不用 ECharts tree：其 orthogonal 布局会把整棵树压缩进固定画布（叶子多时
 * 行高被挤到小于字号 →「文字全挤在一起」），且 label 不支持卡片式盒节点。手绘
 * HTML+SVG 可精确控制行距（固定 V_GAP 不压缩）、文本自适应换行（CSS 天然支持）、
 * 内容宽高超出画布后原生滚动 + 拖拽平移，观感对齐飞书画板思维导图。
 *
 * 行为：
 *   - 数据：buildStrategyMindMap（同目录 strategyMindMap.ts）合并出的「条件前缀树 →
 *     执行操作」语义树；同层完全相同结点合并为一个，文案后缀「（启用 x/共 y）」，
 *     单条停用 →「（已停用）」。
 *   - 节点：卡片式盒节点（圆角边框 + 卡底，max-w-[220px] 自适应换行）；根=橙、
 *     条件=中性卡、操作叶子=语义色描边（暂停红 / 降预算·预警黄 / 其余青，镜像
 *     leo-tag 口径）；全停用子树（enabledCount === 0，根除外）opacity 弱化。
 *   - 折叠：默认展开到根以下第 4 层（更深的结点收起，节点上「+n条」徽标提示被
 *     收起的策略数）；点击有子级的结点折叠/展开；工具栏「展开全部 / 收起默认」。
 *   - 全屏：工具栏「全屏」把脑图铺到整屏固定浮层（LEO §6.6 全屏预览层 z-140，
 *     CSS fixed 覆盖整个浏览器视口，不用 Fullscreen API——无权限/兼容分支）；
 *     浮层内提供「退出全屏」按钮并支持 ESC，退出后恢复卡片内嵌布局。
 *   - 平移：内容宽/高可超出画布；画布内按住拖拽平移（拖动 >4px 时抑制结点点击），
 *     亦可用原生滚动条 / Shift+滚轮横向滚动。
 *   - 悬浮明细：hover 结点浮层列出该结点合并的策略（#id + 启停徽章 + 完整条件组
 *     （倍率模式带基线生效阈值）+ 操作，上限 8 条，超出「…等 N 条」）。
 *   - 策略id（PR-HW-20261102-002）：操作叶子追加「策略id」行（归属策略 id 升序
 *     等宽列出，与列表视图「策略id」列对账口径一致）；中间条件结点不加（合并
 *     策略数多、噪音大），hover 浮层仍可按 id 查明细。
 *   - 数值内联编辑（PR-HW-20261102-001，canEditValue + onSubmitNodeValue 传入时启用）：
 *     数值型条件结点与带参操作叶子（预算降低/提升/调整至）的数值渲染为可点击热点，
 *     点击原位变输入框（Enter/失焦提交、Esc 取消）；指标名称/操作符/周期/动作类型
 *     不可编辑；结点为同条件合并节点，编辑批量应用到途经/归属的全部策略，提交前
 *     弹确认提示（显示影响条数）；校验/批量 PUT/重拉由页面侧回调承担。
 *   - 主题：结点/连线/浮层全部引用 CSS 变量或静态语义色（两主题通用），随
 *     <html>.dark 自动翻转，无需 JS 取色。
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  type WatchStrategyRecord,
  describeCondition,
  describeAction,
} from "../../services/huawei/watchStrategy";
import { pushToast } from "../../hooks/useToast";
import {
  type MindMapNode,
  type ActionTone,
  buildStrategyMindMap,
  validateNodeValue,
} from "./strategyMindMap";

/* ---------- 布局常量（像素） ---------- */
/** 结点盒最大宽度（文本区自适应换行的边界）。 */
const NODE_MAX_W = 220;
/** 结点盒最小宽度（短文案也保持盒感）。 */
const NODE_MIN_W = 56;
/** 水平内边距（对应 px-3）与垂直内边距（对应 py-1.5）。 */
const NODE_PAD_X = 12;
const NODE_PAD_Y = 6;
/** 字号与行高（对应 text-[12px] leading-[18px]），测量须与渲染一致。 */
const FONT_SIZE = 12;
const LINE_H = 18;
/** 操作叶子「策略id」行字号与行高（对应 text-[11px] leading-[16px]），含上方 2px 间距。 */
const ID_FONT_SIZE = 11;
const ID_LINE_H = 16;
/** 层间水平间距（连线跨度）与同层结点垂直间距（固定 → 永不压缩，治本「挤在一起」）。 */
const H_GAP = 44;
const V_GAP = 12;
/** 画布内边距。 */
const PAD = 16;
/** 默认展开深度：根（depth=0）以下 4 层；更深的结点初始折叠。 */
const DEFAULT_EXPAND_DEPTH = 4;
/** tooltip 单结点最多列出的策略条数（超出追加「…等 N 条」）。 */
const TOOLTIP_MAX_ROWS = 8;

/* ---------- 文本测量（不经 DOM，确定性估算；CJK≈字号宽、ASCII≈0.55 字号） ---------- */
// CJK 统一表意、兼容字、全角与中文标点区间（U+1100-11FF / U+2E80-A4CF / U+AC00-D7AF / U+F900-FAFF / U+3000-303F / U+FF00-FFEF）。
const CJK_RE = /[ᄀ-ᇿ⺀-꓏가-힯豈-﫿　-〿＀-￯]/;

/** 估算单行文本渲染宽度（px）。 */
function estimateTextWidth(s: string): number {
  let w = 0;
  for (const ch of s) w += CJK_RE.test(ch) ? FONT_SIZE : FONT_SIZE * 0.55;
  return w;
}

/** 估算结点盒尺寸：宽 = min(文本宽 + 内边距, 上限)；高 = 行数 × 行高 + 内边距 + 边框。
 *  extraText（策略id 行）为独立 block 行：字号 11px（宽度按 ID_FONT_SIZE/FONT_SIZE 折算）、
 *  行高 ID_LINE_H + 2px 上间距，参与宽度取 max、行数独立累加。 */
function measureNode(text: string, hasBadge: boolean, extraText?: string): { w: number; h: number } {
  const badgeW = hasBadge ? 44 : 0; // 「+n条」徽标占位（内联在文本流中）
  const textW = estimateTextWidth(text) + badgeW;
  const contentMax = NODE_MAX_W - NODE_PAD_X * 2;
  const lines = Math.max(1, Math.ceil(textW / contentMax));
  let w = Math.min(Math.ceil(textW) + NODE_PAD_X * 2 + 2, NODE_MAX_W);
  let extraH = 0;
  if (extraText) {
    const idW = estimateTextWidth(extraText) * (ID_FONT_SIZE / FONT_SIZE);
    extraH = Math.max(1, Math.ceil(idW / contentMax)) * ID_LINE_H + 2;
    w = Math.max(w, Math.min(Math.ceil(idW) + NODE_PAD_X * 2 + 2, NODE_MAX_W));
  }
  const h = lines * LINE_H + extraH + NODE_PAD_Y * 2 + 2;
  return { w: Math.max(w, NODE_MIN_W), h };
}

/* ---------- 结点文案与样式 ---------- */
/** 计数后缀（total>1 →「（启用 x/共 y）」；单条停用 →「（已停用）」）。 */
function nodeCountSuffix(node: MindMapNode): string {
  if (node.total > 1) return `（启用 ${node.enabledCount}/共 ${node.total}）`;
  if (node.total === 1 && node.enabledCount === 0) return `（已停用）`;
  return "";
}

/** 结点文案 = label + 计数后缀。 */
function nodeDisplayName(node: MindMapNode): string {
  return node.label + nodeCountSuffix(node);
}

/** 操作叶子「策略id」行文案：归属策略 id 升序列出（#12,#15,#23；strategies 已在
 *  finalize 按 id 升序），与列表视图「策略id」列（等宽字体对账口径）对应。 */
function nodeStrategyIds(node: MindMapNode): string {
  return node.strategies.map((s) => `#${s.id}`).join(",");
}

/** 操作叶子盒节点语义色（静态值两主题通用；描边/底色口径镜像 index.css .leo-tag）。 */
const TONE_BOX: Record<ActionTone, string> = {
  danger: "border-[rgba(248,113,113,0.5)] bg-[rgba(248,113,113,0.10)]",
  warning: "border-[rgba(251,191,36,0.5)] bg-[rgba(251,191,36,0.12)]",
  info: "border-[rgba(59,130,246,0.5)] bg-[rgba(59,130,246,0.10)]",
};

/** 结点盒样式：根=橙、条件=中性卡（主题变量）、操作=语义色；全停用子树弱化。 */
function nodeBoxClass(node: MindMapNode): string {
  const base =
    node.kind === "root"
      ? "border-[rgba(255,107,53,0.45)] bg-[rgba(255,107,53,0.10)] font-semibold"
      : node.kind === "action"
        ? `${TONE_BOX[node.actionTone ?? "info"]} font-medium`
        : "border-line-strong bg-da-card";
  const dimmed = node.kind !== "root" && node.enabledCount === 0 ? " opacity-40" : "";
  return `${base} text-text-strong${dimmed}`;
}

/* ---------- 布局（tidy tree：叶子逐行排列，内部结点取子级中心；列宽按层取最大） ---------- */
interface LaidNode {
  id: string;
  node: MindMapNode;
  depth: number;
  x: number;
  y: number;
  w: number;
  h: number;
  /** 中心 y（连线锚点）。 */
  cy: number;
  collapsed: boolean;
  children: LaidNode[];
}

interface MindMapLayout {
  nodes: LaidNode[];
  edges: { key: string; x1: number; y1: number; x2: number; y2: number }[];
  totalW: number;
  totalH: number;
}

/** 结点路径 id（合并键仅同级唯一，路径拼揍保证全树唯一，供折叠状态与 React key）。 */
function childPath(parentPath: string, n: MindMapNode): string {
  return `${parentPath}/${n.kind === "action" ? "a" : "c"}:${n.key}`;
}

/** 默认折叠集合：depth ≥ DEFAULT_EXPAND_DEPTH 且有子级的结点（即根以下只展开 4 层）。 */
function defaultCollapsedIds(root: MindMapNode): Set<string> {
  const ids = new Set<string>();
  (function walk(n: MindMapNode, path: string, depth: number) {
    if (depth >= DEFAULT_EXPAND_DEPTH && n.children.length > 0) ids.add(path);
    for (const c of n.children) walk(c, childPath(path, c), depth + 1);
  })(root, "root", 0);
  return ids;
}

/** 语义树 + 折叠状态 → 可见树布局（坐标/尺寸/连线）。 */
function layoutMindMap(root: MindMapNode, collapsedIds: Set<string>): MindMapLayout {
  function buildVisible(n: MindMapNode, path: string, depth: number): LaidNode {
    const collapsed = collapsedIds.has(path);
    // 操作叶子追加「策略id」行（参与尺寸测量，保证盒节点放得下 id 列表）
    const idText = n.kind === "action" && n.strategies.length > 0 ? nodeStrategyIds(n) : undefined;
    const { w, h } = measureNode(nodeDisplayName(n), collapsed, idText);
    return {
      id: path,
      node: n,
      depth,
      x: 0,
      y: 0,
      w,
      h,
      cy: 0,
      collapsed,
      children: collapsed
        ? []
        : n.children.map((c) => buildVisible(c, childPath(path, c), depth + 1)),
    };
  }
  const vroot = buildVisible(root, "root", 0);

  // 每层列宽 = 该层可见结点最大宽；列 x = 前层列 x + 列宽 + 水平间距。
  const colW: number[] = [];
  (function scan(n: LaidNode) {
    colW[n.depth] = Math.max(colW[n.depth] ?? 0, n.w);
    n.children.forEach(scan);
  })(vroot);
  const colX: number[] = [PAD];
  for (let d = 0; d < colW.length; d++) {
    colX[d + 1] = colX[d] + (colW[d] ?? 0) + H_GAP;
  }

  // 后序赋 y：叶子占一行（游标推进），内部结点取首末子级中心。
  let cursor = PAD;
  (function place(n: LaidNode) {
    n.x = colX[n.depth];
    if (n.children.length === 0) {
      n.y = cursor;
      n.cy = n.y + n.h / 2;
      cursor += n.h + V_GAP;
    } else {
      n.children.forEach(place);
      n.cy = (n.children[0].cy + n.children[n.children.length - 1].cy) / 2;
      n.y = n.cy - n.h / 2;
    }
  })(vroot);

  // 拍平结点 + 生成连线（父右缘中点 → 子左缘中点）。
  const nodes: LaidNode[] = [];
  const edges: MindMapLayout["edges"] = [];
  (function flat(n: LaidNode) {
    nodes.push(n);
    for (const c of n.children) {
      edges.push({ key: `${n.id}->${c.id}`, x1: n.x + n.w, y1: n.cy, x2: c.x, y2: c.cy });
      flat(c);
    }
  })(vroot);

  const maxDepth = colW.length - 1;
  const totalW = colX[maxDepth] + (colW[maxDepth] ?? 0) + PAD;
  const totalH = Math.max(cursor - V_GAP + PAD, 120);
  return { nodes, edges, totalW, totalH };
}

/* ---------- tooltip（浮层为普通 DOM，主题变量直接生效；内容同表格口径） ---------- */
interface TipState {
  node: MindMapNode;
  x: number;
  y: number;
}

interface TooltipBodyProps {
  node: MindMapNode;
  targetSxfl: number;
  targetCost: number;
  /** 数值内联编辑是否启用（canEditValue && onSubmitNodeValue）。 */
  editEnabled: boolean;
}

/** 悬浮明细：结点文案 + 启用计数 + 合并策略清单（#id + 启停徽章 + 条件组 + 操作）。 */
function TooltipBody({ node, targetSxfl, targetCost, editEnabled }: TooltipBodyProps) {
  const rows = node.strategies.slice(0, TOOLTIP_MAX_ROWS);
  return (
    <>
      <div className="font-semibold text-text-strong">{node.label}</div>
      <div className="mb-1.5 mt-0.5 text-[11px] text-text-muted">
        启用 {node.enabledCount} / 共 {node.total} 条
      </div>
      {rows.map((s) => (
        <div key={s.id} className="my-1">
          <span className="font-mono text-text-muted-weak">#{s.id}</span>{" "}
          <span className={`leo-tag ${s.is_enabled ? "is-success" : "is-danger"}`}>
            {s.is_enabled ? "启用" : "停用"}
          </span>{" "}
          <span className="text-text-body">
            {s.conditions.map((c, i) => (
              <span key={i}>
                {i > 0 ? <span className="text-text-muted-weak"> 且 </span> : null}
                {describeCondition(c, targetSxfl, targetCost)}
              </span>
            ))}
          </span>{" "}
          <span className="text-text-muted-weak">→</span>{" "}
          <span className="font-semibold text-text-strong">
            {describeAction(s.action_type, s.action_value)}
          </span>
        </div>
      ))}
      {node.strategies.length > TOOLTIP_MAX_ROWS ? (
        <div className="text-[11px] text-text-muted-weak">…等 {node.strategies.length} 条</div>
      ) : null}
      {editEnabled && node.editable ? (
        <div className="mt-1 text-[11px] text-text-muted-weak">
          点击节点数值可直接修改（批量应用于 {node.total} 条策略）
        </div>
      ) : node.kind === "action" ? (
        <div className="mt-1 text-[11px] text-text-muted-weak">编辑请切回列表视图</div>
      ) : null}
    </>
  );
}

/* ---------- 组件 ---------- */
interface WatchStrategyMindMapProps {
  /** 全量策略（页面已一次性拉取 ≤200 条）。 */
  strategies: WatchStrategyRecord[];
  /** 目标费率基线（倍率模式条件「目标费率×x(=生效值)」渲染用）。 */
  targetSxfl: number;
  /** 目标成本基线（元，「目标成本×x(=生效值)」渲染用）。 */
  targetCost: number;
  /** 是否启用数值内联编辑（页面侧权限键 huawei:watch-strategy）；false/缺省 = 纯预览。 */
  canEditValue?: boolean;
  /** 数值编辑提交回调（确认弹窗后触发）：页面侧负责校验/批量 PUT/toast/重拉。 */
  onSubmitNodeValue?: (node: MindMapNode, newValue: number) => Promise<void>;
}

export default function WatchStrategyMindMap({
  strategies,
  targetSxfl,
  targetCost,
  canEditValue = false,
  onSubmitNodeValue,
}: WatchStrategyMindMapProps) {
  const treeData = useMemo(
    () => buildStrategyMindMap(strategies, { targetSxfl, targetCost }),
    [strategies, targetSxfl, targetCost],
  );

  /** 数值内联编辑总开关（权限 + 回调齐备才启用）。 */
  const isEditEnabled = canEditValue && !!onSubmitNodeValue;

  // 折叠状态：路径 id 集合；初始 = 默认（根以下 4 层），数据变化时重置为默认。
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() =>
    defaultCollapsedIds(treeData),
  );
  // 内联编辑态：editingId=编辑中结点路径 id；draft=输入中间态（字符串，提交时 parseFloat）。
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  // 批量确认弹窗：提交前提示影响条数（合并结点 = 批量应用途经/归属的全部策略）。
  const [pendingEdit, setPendingEdit] = useState<{ node: MindMapNode; value: number } | null>(
    null,
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setCollapsedIds(defaultCollapsedIds(treeData));
    // 数据重建（如编辑后重拉）时清空编辑/确认态，避免引用过期结点
    setEditingId(null);
    setPendingEdit(null);
  }, [treeData]);

  const layout = useMemo(() => layoutMindMap(treeData, collapsedIds), [treeData, collapsedIds]);

  const [tip, setTip] = useState<TipState | null>(null);

  /** 全屏：true 时脑图渲染为整屏固定浮层（LEO §6.6 全屏预览层 z-140），ESC /「退出全屏」按钮还原。 */
  const [isFullscreen, setIsFullscreen] = useState(false);

  // 全屏期间：ESC 退出 + 锁定页面滚动；退出全屏 / 组件卸载（如切回列表视图）时还原。
  useEffect(() => {
    if (!isFullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isFullscreen]);

  /* 拖拽平移：容器 overflow-auto + 按住拖动改 scrollLeft/Top（飞书画板手感）；
     拖动 >4px 置 moved，结点 onClick 读取并抑制（避免拖拽误触折叠）。 */
  const panRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ x: number; y: number; left: number; top: number; moved: boolean } | null>(
    null,
  );

  function onPanMouseDown(e: React.MouseEvent) {
    const el = panRef.current;
    if (!el) return;
    e.preventDefault(); // 避免拖拽时进入文本选择
    dragRef.current = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop, moved: false };
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      const pan = panRef.current;
      if (!d || !pan) return;
      const dx = ev.clientX - d.x;
      const dy = ev.clientY - d.y;
      if (Math.abs(dx) + Math.abs(dy) > 4) d.moved = true;
      pan.scrollLeft = d.left - dx;
      pan.scrollTop = d.top - dy;
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // click 在 mouseup 之后触发，延迟清除以便结点 onClick 读到 moved。
      setTimeout(() => {
        dragRef.current = null;
      }, 0);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  /** 点击有子级的结点折叠/展开（拖拽后的点击被抑制）。 */
  function onNodeClick(n: LaidNode) {
    if (dragRef.current?.moved) return;
    if (n.node.children.length === 0) return;
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(n.id)) next.delete(n.id);
      else next.add(n.id);
      return next;
    });
  }

  function showTip(node: MindMapNode, e: React.MouseEvent) {
    setTip({ node, x: e.clientX, y: e.clientY });
  }
  function moveTip(e: React.MouseEvent) {
    setTip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY } : prev));
  }

  /** 提交内联编辑（Enter/失焦）：解析+校验失败 toast 并保持编辑态；未改动静默退出；
   *  合法且已改动 → 进入批量确认弹窗（editingId 防御：Enter 提交后 input 卸载触发
   *  blur 的重复提交直接忽略）。 */
  function commitEdit(node: MindMapNode) {
    if (editingId === null) return;
    const v = Number.parseFloat(draft);
    if (!Number.isFinite(v)) {
      pushToast("数值须为可解析数字", "error");
      return;
    }
    const err = validateNodeValue(node, v);
    if (err) {
      pushToast(err, "error");
      return;
    }
    setEditingId(null);
    if (node.editable && v === Number.parseFloat(node.editable.valueText)) return;
    setPendingEdit({ node, value: v });
  }

  /** 确认弹窗「确认修改」：批量更新由页面回调承担（校验/PUT/toast/重拉）。 */
  async function confirmPendingEdit() {
    if (!pendingEdit || submitting || !onSubmitNodeValue) return;
    setSubmitting(true);
    try {
      await onSubmitNodeValue(pendingEdit.node, pendingEdit.value);
    } finally {
      setSubmitting(false);
      setPendingEdit(null);
    }
  }

  /** 结点内容：可编辑结点拆段渲染（前缀 + 数值热点/输入框 + 后缀 + 计数后缀，
   *  拼接文本与 nodeDisplayName 一致，宽度测量无需区分）；否则整串文本。 */
  function renderNodeContent(n: LaidNode) {
    const ed = n.node.editable;
    if (!ed || !isEditEnabled) return nodeDisplayName(n.node);
    const countSuffix = nodeCountSuffix(n.node);
    if (editingId === n.id) {
      return (
        <>
          {ed.prefix}
          <input
            autoFocus
            value={draft}
            aria-label="修改数值"
            className="mx-0.5 w-16 rounded border border-accent-cta/50 bg-da-inset px-1 py-0 text-center text-[12px] leading-[16px] text-text-strong outline-none focus:border-accent-cta"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                commitEdit(n.node);
              } else if (e.key === "Escape") {
                e.stopPropagation(); // 避免触发全屏态的 ESC 退出
                setEditingId(null);
              }
            }}
            onBlur={() => commitEdit(n.node)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
          {ed.suffix}
          {countSuffix}
        </>
      );
    }
    return (
      <>
        {ed.prefix}
        <span
          className="cursor-text border-b border-dashed border-accent-cta/70 text-accent-cta"
          title="点击修改数值"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation(); // 避免触发结点折叠
            setEditingId(n.id);
            setDraft(ed.valueText);
          }}
        >
          {ed.valueText}
        </span>
        {ed.suffix}
        {countSuffix}
      </>
    );
  }

  return (
    // 全屏：整屏固定浮层（LEO §6.6 全屏预览层 z-140，bg-da-app 不透出下层页面）；
    // 普通：卡片内嵌。折叠状态 / 拖拽 / 浮层两态共用，仅容器布局类不同。
    <div
      className={
        isFullscreen
          ? "fixed inset-0 z-[140] flex flex-col bg-da-app p-4 md:p-6"
          : undefined
      }
    >
      {/* 工具栏：操作提示 + 展开控制 + 全屏切换 */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[12px] text-text-muted">
          按住拖拽平移 · 点击结点折叠/展开 · 悬浮结点查看合并策略明细
          {isEditEnabled ? " · 点击数值可直接修改" : ""}
          {isFullscreen ? " · ESC 退出全屏" : ""}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            className="leo-btn-ghost px-2 py-1 text-[12px]"
            onClick={() => setCollapsedIds(new Set())}
          >
            展开全部
          </button>
          <button
            type="button"
            className="leo-btn-ghost px-2 py-1 text-[12px]"
            onClick={() => setCollapsedIds(defaultCollapsedIds(treeData))}
          >
            收起默认
          </button>
          {/* 全屏 / 退出全屏（LEO §6.8：内联 SVG 图标，stroke=currentColor） */}
          <button
            type="button"
            className="leo-btn-ghost inline-flex items-center gap-1 px-2 py-1 text-[12px]"
            onClick={() => setIsFullscreen((v) => !v)}
          >
            {isFullscreen ? (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M4 14h6v6" />
                <path d="M20 10h-6V4" />
                <path d="M14 10l7-7" />
                <path d="M3 21l7-7" />
              </svg>
            ) : (
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M15 3h6v6" />
                <path d="M9 21H3v-6" />
                <path d="M21 3l-7 7" />
                <path d="M3 21l7-7" />
              </svg>
            )}
            {isFullscreen ? "退出全屏" : "全屏"}
          </button>
        </div>
      </div>

      {/* 画布：内容宽高可超出视口，拖拽/滚动查看；全屏时 flex-1 铺满剩余高度 */}
      <div
        ref={panRef}
        onMouseDown={onPanMouseDown}
        className={`relative w-full cursor-grab select-none overflow-auto rounded-lg border border-line-subtle bg-da-inset active:cursor-grabbing ${
          isFullscreen ? "min-h-0 flex-1" : "h-[600px]"
        }`}
      >
        <div className="relative" style={{ width: layout.totalW, height: layout.totalH }}>
          <svg
            className="absolute left-0 top-0"
            width={layout.totalW}
            height={layout.totalH}
            aria-hidden
          >
            {layout.edges.map((e) => {
              const dx = Math.max(24, (e.x2 - e.x1) / 2);
              return (
                <path
                  key={e.key}
                  className="leo-mindmap-edge"
                  d={`M ${e.x1} ${e.y1} C ${e.x1 + dx} ${e.y1}, ${e.x2 - dx} ${e.y2}, ${e.x2} ${e.y2}`}
                />
              );
            })}
          </svg>
          {layout.nodes.map((n) => (
            <div
              key={n.id}
              className={`absolute box-border break-words rounded-lg border px-3 py-1.5 text-[12px] leading-[18px] transition-opacity ${nodeBoxClass(n.node)} ${
                n.node.children.length > 0 ? "cursor-pointer" : "cursor-default"
              }`}
              style={{ left: n.x, top: n.y, width: n.w }}
              onClick={() => onNodeClick(n)}
              onMouseEnter={(e) => showTip(n.node, e)}
              onMouseMove={moveTip}
              onMouseLeave={() => setTip(null)}
            >
              {renderNodeContent(n)}
              {n.collapsed ? (
                <span className="ml-1.5 inline-block rounded-full bg-accent-cta/15 px-1.5 align-middle text-[10px] leading-4 text-accent-cta">
                  +{n.node.total}条
                </span>
              ) : null}
              {/* 操作叶子：「策略id」行（等宽小字，与列表视图策略id列对账口径一致） */}
              {n.node.kind === "action" && n.node.strategies.length > 0 ? (
                <div className="mt-0.5 font-mono text-[11px] leading-[16px] text-text-muted">
                  {nodeStrategyIds(n.node)}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      </div>

      {/* 悬浮明细浮层（复用 .leo-chart-tooltip 容器样式；横向越界时左收；
          z-160 高于全屏浮层 z-140，保证全屏态 hover 仍可见） */}
      {tip ? (
        <div
          className="leo-chart-tooltip pointer-events-none fixed z-[160] max-w-[560px] p-3 text-[12px]"
          style={{
            left: Math.min(tip.x + 14, window.innerWidth - 580),
            top: tip.y + 16,
          }}
        >
          <TooltipBody
            node={tip.node}
            targetSxfl={targetSxfl}
            targetCost={targetCost}
            editEnabled={isEditEnabled}
          />
        </div>
      ) : null}

      {/* 数值编辑批量确认弹窗（LEO §6.6 浮层：z-170 高于 tooltip z-160 与全屏层 z-140；
          合并结点 = 批量应用，须明示影响条数） */}
      {pendingEdit ? (
        <div
          className="fixed inset-0 z-[170] flex items-center justify-center bg-black/45"
          onClick={() => setPendingEdit(null)}
        >
          <div
            className="leo-card w-[min(92vw,380px)] rounded-xl p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="font-display text-[14px] font-semibold text-text-emphasis">
              确认修改数值
            </div>
            <div className="mt-2 text-[12.5px] leading-relaxed text-text-body">
              「{pendingEdit.node.label}」的新数值为{" "}
              <span className="font-semibold text-accent-cta">{pendingEdit.value}</span>
              ，将同步修改 {pendingEdit.node.total} 条策略（启用{" "}
              {pendingEdit.node.enabledCount} 条）。
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                className="leo-btn-ghost px-4 py-1.5 text-[12.5px]"
                onClick={() => setPendingEdit(null)}
              >
                取消
              </button>
              <button
                type="button"
                className="leo-cta-primary w-auto px-5 py-1.5 text-[12.5px]"
                disabled={submitting}
                onClick={confirmPendingEdit}
              >
                {submitting ? "提交中…" : "确认修改"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
